/**
 * Mesma lógica de AwarenessStageTable: lista plana id/id_pai → árvore ordenada.
 * Normaliza id/id_pai (string vs number vs BigInt do MySQL) para o Map não quebrar.
 */
function normId(v) {
  if (v == null || v === "") return null;
  const n = typeof v === "bigint" ? Number(v) : Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function buildAwarenessGroupTree(items = []) {
  const byId = new Map();
  items.forEach((it) => {
    const id = normId(it.id);
    if (id == null) return;
    byId.set(id, { ...it, id, children: [] });
  });
  const roots = [];
  byId.forEach((node) => {
    const p = normId(node.id_pai);
    if (p != null && byId.has(p)) {
      byId.get(p).children.push(node);
    } else {
      roots.push(node);
    }
  });
  const sortRec = (arr) => {
    arr.sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
    arr.forEach((n) => sortRec(n.children || []));
    return arr;
  };
  return sortRec(roots);
}

export function awarenessStageHasChildren(node) {
  return Array.isArray(node?.children) && node.children.length > 0;
}

/** Inclui o próprio estágio e todos os descendentes (para listar clientes no pai). */
export function collectAwarenessGroupIds(node) {
  const ids = [];
  const walk = (current) => {
    if (!current) return;
    const id = Number(current.id);
    if (Number.isFinite(id)) ids.push(id);
    (current.children || []).forEach(walk);
  };
  walk(node);
  return [...new Set(ids)];
}

/** IDs da subárvore de rootId, sem incluir o próprio root (não podem ser etapa pai). */
export function collectAwarenessDescendantIds(items = [], rootId) {
  const rid = normId(rootId);
  if (rid == null) return [];
  const childrenByParent = new Map();
  for (const it of items || []) {
    const id = normId(it.id);
    const p = normId(it.id_pai);
    if (id == null || p == null) continue;
    if (!childrenByParent.has(p)) childrenByParent.set(p, []);
    childrenByParent.get(p).push(id);
  }
  const out = [];
  const queue = [...(childrenByParent.get(rid) || [])];
  const seen = new Set();
  while (queue.length) {
    const id = queue.shift();
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    for (const ch of childrenByParent.get(id) || []) queue.push(ch);
  }
  return out;
}

export function splitAwarenessPipelineStages(stages = []) {
  const virtual = [];
  const catalog = [];
  for (const stage of stages || []) {
    if (stage?.virtual || Number(stage.id) === 0) virtual.push(stage);
    else catalog.push(stage);
  }
  return { virtual, catalog };
}

export function attachAwarenessSubtreeTotals(nodes = []) {
  const walk = (node) => {
    const own = Number(node.total) || 0;
    const kids = (node.children || []).reduce((sum, child) => sum + walk(child), 0);
    node.subtreeTotal = own + kids;
    node.childCount = (node.children || []).length;
    return node.subtreeTotal;
  };
  nodes.forEach(walk);
  return nodes;
}

export function buildAwarenessPipelineForest(stages = []) {
  const { virtual, catalog } = splitAwarenessPipelineStages(stages);
  const roots = attachAwarenessSubtreeTotals(buildAwarenessGroupTree(catalog));
  return { virtual, roots };
}

/**
 * Clientes ainda no nível atual (id_grupo do pai), sem subetapa.
 * Sem os filhos no nó, a listagem usa só o id do pai — não a subárvore.
 */
export function buildAwarenessLevelRemainder(parent) {
  if (!parent || !awarenessStageHasChildren(parent)) return null;
  const ownTotal = Number(parent.total) || 0;
  if (ownTotal <= 0) return null;
  return {
    ...parent,
    children: [],
    childCount: 0,
    remainder: true,
    remainderOf: Number(parent.id),
    remainderName: parent.nome,
    subtreeTotal: ownTotal,
  };
}

/** Sem grupo na raiz e nas subetapas; neste nível, também quem ficou só no pai. */
export function visibleAwarenessStages(forest, path = []) {
  const last = path[path.length - 1];
  const level = last ? last.children || [] : forest?.roots || [];
  const remainder = buildAwarenessLevelRemainder(last);
  return [...(forest?.virtual || []), ...(remainder ? [remainder] : []), ...level];
}

export function autoOpenAwarenessSingletons(roots = []) {
  const pathIds = [];
  let level = roots;
  while (Array.isArray(level) && level.length === 1 && awarenessStageHasChildren(level[0])) {
    pathIds.push(level[0].id);
    level = level[0].children;
  }
  return { pathIds, visible: Array.isArray(level) ? level : [] };
}

function sameProjectId(rowProjeto, targetPid) {
  const a = rowProjeto == null ? "" : String(rowProjeto).trim();
  const b = String(targetPid).trim();
  if (a === b) return true;
  const na = Number(a);
  const nb = Number(b);
  return Number.isFinite(na) && Number.isFinite(nb) && na === nb;
}

/**
 * Etapas de consciência no setup: mesma visão da tela /awareness-stages (raízes da árvore),
 * apenas do id_projeto atual.
 */
export function awarenessSetupLevelsFromGroups(groups, idProjeto) {
  const pid = Number(idProjeto);
  if (!Number.isFinite(pid) || pid <= 0) return [];
  const all = (Array.isArray(groups) ? groups : []).filter((g) => Number(g.excluido) !== 1);
  let flat = all.filter((g) => sameProjectId(g.id_projeto, pid));
  // Mesmo comportamento da tabela em /awareness-stages: usa o payload já filtrado pela API;
  // se não houver linhas para este id_projeto mas a lista veio preenchida (legado), monta a árvore com tudo.
  if (!flat.length && all.length) {
    const distinct = new Set(all.map((g) => Number(g.id_projeto)));
    if (distinct.size === 1) flat = all;
  }
  if (!flat.length) return [];
  const roots = buildAwarenessGroupTree(flat);
  return roots
    .map((r) => ({
      title: String(r.nome || "").trim(),
      description: "",
    }))
    .filter((x) => x.title);
}
